import React from 'react';
import { Chip, Typography } from '@mui/material';
import type { ChipProps } from '@mui/material';
import { CheckCircle, Cancel, Warning, HelpOutline } from '@mui/icons-material';

type ResultType = 'Pass' | 'Fail' | 'Limited' | 'Unknown' | string;

type Props = {
  result?: ResultType | null;
  size?: ChipProps['size'];
  showIcon?: boolean;
  variant?: ChipProps['variant'];
  sx?: ChipProps['sx'];
  emptyText?: string;
};

export const ResultChip: React.FC<Props> = ({
  result,
  size = 'small',
  showIcon = true,
  variant = 'filled',
  sx = {},
  emptyText = '—'
}) => {
  if (
    !result ||
    result === 'Unknown' ||
    result === 'null' ||
    result.trim() === ''
  ) {
    return <Typography variant="body2">{emptyText}</Typography>;
  }

  const getChipProps = (): {
    color: ChipProps['color'];
    icon?: React.ReactElement;
  } => {
    const normalizedResult = result.toLowerCase();

    if (normalizedResult === 'pass' || normalizedResult === 'passed') {
      return {
        color: 'success',
        icon: showIcon ? <CheckCircle /> : undefined
      };
    }

    if (normalizedResult === 'fail' || normalizedResult === 'failed') {
      return {
        color: 'error',
        icon: showIcon ? <Cancel /> : undefined
      };
    }

    if (normalizedResult === 'limited') {
      return {
        color: 'warning',
        icon: showIcon ? <Warning /> : undefined
      };
    }

    return {
      color: 'default',
      icon: showIcon ? <HelpOutline /> : undefined
    };
  };

  const chipProps = getChipProps();

  return (
    <Chip
      size={size}
      label={result}
      color={chipProps.color}
      icon={chipProps.icon}
      variant={variant}
      sx={sx}
    />
  );
};
